import { c as _c } from "react/compiler-runtime";
import { publishEvent, TrackImpression } from '@udemy-v2/event-tracking';
import { Avatar } from '@udemy-v2/react-core-components';
import { StarRating } from '@udemy-v2/react-merchandising-components';
import { ShowMore } from '@udemy-v2/react-reveal-components';
import { ShowMoreButton } from './show-more-button.react-component';
import classNames from 'classnames';
import React, { useEffect, useLayoutEffect, useState, useRef, useCallback } from 'react';
import { useInView } from 'react-intersection-observer';
import { Review as ReviewType } from '@/types/review';
import { ReviewExpandEvent, ReviewImpressionEvent } from '@/features/course-landing-page/events/events';
import makeRenderContent from '@/lib/make-render-content';
import styles from './review.module.scss';
import showMoreButtonStyles from './show-more-button.module.scss';
import { ReviewsModalButton } from '../review-modal/reviews-modal-button.react-component';
import { InstructorResponse } from '../instructor-response.react-component';
import { ReviewReport } from '../review-report/review-report.react-component';
import { ReviewFeedback } from './review-feedback/review-feedback.react-component';
import { RelativeDuration, useI18n } from '@udemy-v2/i18n';
import { useUserHasOrganization } from '@/lib/hooks/use-user-has-organization';
interface ReviewComponentProps {
  review: ReviewType;
  searchedTerm?: string;
  frontendTrackingId?: string;
  reviewableObjectId: number;
  reviewableObjectType: string;
  className?: string;
  isDesktopLayout?: boolean;
  shouldUseInlineShowMore?: boolean;
  shouldUseDeattachedDropdown?: boolean;
  instructorResponseVisibility?: 'visible' | 'hidden' | 'seo-only';
  onShowMoreModalTriggerClick?: () => void;
  isModal?: boolean;
  isInMobileCarousel?: boolean;
}
export const Review = t0 => {
  const $ = _c(136);
  const {
    review,
    searchedTerm,
    frontendTrackingId,
    reviewableObjectId,
    reviewableObjectType,
    className,
    isDesktopLayout: t1,
    shouldUseInlineShowMore: t2,
    shouldUseDeattachedDropdown: t3,
    instructorResponseVisibility: t4,
    onShowMoreModalTriggerClick,
    isModal: t5,
    isInMobileCarousel: t6
  } = t0;
  const isDesktopLayout = t1 === undefined ? false : t1;
  const shouldUseInlineShowMore = t2 === undefined ? true : t2;
  const shouldUseDeattachedDropdown = t3 === undefined ? false : t3;
  const instructorResponseVisibility = t4 === undefined ? "hidden" : t4;
  const isModal = t5 === undefined ? false : t5;
  const isInMobileCarousel = t6 === undefined ? false : t6;
  const {
    gettext
  } = useI18n();
  const isOrgUser = useUserHasOrganization();
  const reviewContentRef = useRef(null);
  const reviewContainerElementRef = useRef(null);
  const [shouldShowMoreButton, setShouldShowMoreButton] = useState(false);
  let t7;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t7 = {
      threshold: 0,
      triggerOnce: false
    };
    $[0] = t7;
  } else {
    t7 = $[0];
  }
  const {
    ref: intersectionRef,
    inView
  } = useInView(t7);
  let t8;
  if ($[1] !== intersectionRef) {
    t8 = node => {
      reviewContainerElementRef.current = node;
      intersectionRef(node);
    };
    $[1] = intersectionRef;
    $[2] = t8;
  } else {
    t8 = $[2];
  }
  const setRefs = t8;
  const round = _temp;
  let t9;
  if ($[3] !== inView) {
    t9 = () => {
      if (inView && reviewContentRef.current) {
        const scrollHeight = round(reviewContentRef.current.scrollHeight);
        const clientHeight = round(reviewContentRef.current.clientHeight);
        const hasOverflow = scrollHeight > clientHeight;
        setShouldShowMoreButton(hasOverflow);
      }
    };
    $[3] = inView;
    $[4] = t9;
  } else {
    t9 = $[4];
  }
  const checkOverflow = t9;
  let t10;
  let t11;
  if ($[5] !== checkOverflow || $[6] !== inView) {
    t10 = () => {
      if (inView) {
        checkOverflow();
      }
    };
    t11 = [inView, checkOverflow];
    $[5] = checkOverflow;
    $[6] = inView;
    $[7] = t10;
    $[8] = t11;
  } else {
    t10 = $[7];
    t11 = $[8];
  }
  useLayoutEffect(t10, t11);
  let t12;
  let t13;
  if ($[9] !== checkOverflow || $[10] !== inView) {
    t12 = () => {
      if (!inView) {
        return;
      }
      const container = reviewContainerElementRef.current;
      if (!container) {
        return;
      }
      const resizeObserver = new ResizeObserver(() => {
        checkOverflow();
      });
      resizeObserver.observe(container);
      return () => {
        resizeObserver.disconnect();
      };
    };
    t13 = [inView, checkOverflow];
    $[9] = checkOverflow;
    $[10] = inView;
    $[11] = t12;
    $[12] = t13;
  } else {
    t12 = $[11];
    t13 = $[12];
  }
  useEffect(t12, t13);
  let t14;
  if ($[13] !== frontendTrackingId || $[14] !== review.id || $[15] !== review.response || $[16] !== reviewableObjectId || $[17] !== reviewableObjectType) {
    t14 = () => {
      if (!frontendTrackingId) {
        return;
      }
      const instructorResponses = review.response ? [{
        responseId: Number(review.response.id),
        instructorTrackingId: review.response.user.tracking_id
      }] : [];
      publishEvent(new ReviewImpressionEvent({
        courseTrackingId: frontendTrackingId,
        reviewId: Number(review.id),
        isFeatured: false,
        instructorResponses,
        reviewableObjectId,
        reviewableObjectType
      }));
    };
    $[13] = frontendTrackingId;
    $[14] = review.id;
    $[15] = review.response;
    $[16] = reviewableObjectId;
    $[17] = reviewableObjectType;
    $[18] = t14;
  } else {
    t14 = $[18];
  }
  const trackReviewImpression = t14;
  let t15;
  if ($[19] !== frontendTrackingId || $[20] !== review.id || $[21] !== reviewableObjectId || $[22] !== reviewableObjectType) {
    t15 = () => {
      if (frontendTrackingId) {
        publishEvent(new ReviewExpandEvent({
          courseTrackingId: frontendTrackingId,
          reviewId: Number(review.id),
          isFeatured: false,
          reviewableObjectId,
          reviewableObjectType
        }));
      }
    };
    $[19] = frontendTrackingId;
    $[20] = review.id;
    $[21] = reviewableObjectId;
    $[22] = reviewableObjectType;
    $[23] = t15;
  } else {
    t15 = $[23];
  }
  const handleInlineShowMoreToggle = t15;
  let t16;
  if ($[24] !== onShowMoreModalTriggerClick) {
    t16 = () => {
      if (onShowMoreModalTriggerClick) {
        onShowMoreModalTriggerClick();
      }
    };
    $[24] = onShowMoreModalTriggerClick;
    $[25] = t16;
  } else {
    t16 = $[25];
  }
  const handleShowMoreInModalClick = t16;
  let T0;
  let renderInstructorResponse;
  let t17;
  let t18;
  let t19;
  let t20;
  let t21;
  let t22;
  let t23;
  if ($[26] !== className || $[27] !== frontendTrackingId || $[28] !== gettext || $[29] !== handleInlineShowMoreToggle || $[30] !== handleShowMoreInModalClick || $[31] !== instructorResponseVisibility || $[32] !== isDesktopLayout || $[33] !== isInMobileCarousel || $[34] !== isModal || $[35] !== isOrgUser || $[36] !== review.content || $[37] !== review.createdAt || $[38] !== review.id || $[39] !== review.rating || $[40] !== review.response || $[41] !== review.user || $[42] !== reviewableObjectId || $[43] !== reviewableObjectType || $[44] !== searchedTerm || $[45] !== setRefs || $[46] !== shouldShowMoreButton || $[47] !== shouldUseDeattachedDropdown || $[48] !== shouldUseInlineShowMore || $[49] !== trackReviewImpression) {
    const renderReviewContent = () => {
      const contentAsHtmlWithParagraphs = makeRenderContent(searchedTerm)(review.content ?? "", true);
      const contentAsHtmlWithoutParagraphs = makeRenderContent(searchedTerm)(review.content ?? "", false);
      if (shouldUseInlineShowMore) {
        return <ShowMore collapsedHeight={180} contentClassName="ud-text-md" buttonComponent={ShowMoreButton} hideIcons={true} withGradient={true} onToggle={handleInlineShowMoreToggle}><div dangerouslySetInnerHTML={{
            __html: contentAsHtmlWithParagraphs
          }} /></ShowMore>;
      }
      return <><div ref={reviewContentRef} className={classNames("ud-text-md", styles["review-content-with-modal-trigger"], {
          [styles["with-show-more-button"]]: shouldShowMoreButton
        })} dangerouslySetInnerHTML={{
          __html: contentAsHtmlWithoutParagraphs
        }} />{shouldShowMoreButton && <ReviewsModalButton frontendTrackingId={frontendTrackingId} reviewableObjectType={reviewableObjectType} reviewableObjectId={reviewableObjectId} label={gettext("Show more")} udStyle="ghost" fullWidth={false} onClick={handleShowMoreInModalClick} className={showMoreButtonStyles.button} />}</>;
    };
    let t24;
    if ($[59] !== instructorResponseVisibility || $[60] !== review.response) {
      t24 = () => {
        if (!review.response?.content || instructorResponseVisibility === "hidden") {
          return null;
        }
        if (instructorResponseVisibility === "seo-only") {
          return <div className={styles["instructor-response-seo"]}>{review.response.content}</div>;
        }
        return <InstructorResponse content={review.response.content.split(/\n+/).map(_temp2)} user={review.response.user} formattedTimestampSince={review.response.createdAt} />;
      };
      $[59] = instructorResponseVisibility;
      $[60] = review.response;
      $[61] = t24;
    } else {
      t24 = $[61];
    }
    renderInstructorResponse = t24;
    const t25 = isDesktopLayout && !isModal && styles["impression-wrapper"];
    if ($[62] !== t25) {
      t23 = classNames(t25);
      $[62] = t25;
      $[63] = t23;
    } else {
      t23 = $[63];
    }
    T0 = TrackImpression;
    t22 = trackReviewImpression;
    t19 = setRefs;
    const t26 = className ?? null;
    const t27 = isDesktopLayout && shouldUseInlineShowMore;
    if ($[64] !== isDesktopLayout || $[65] !== isInMobileCarousel || $[66] !== t26 || $[67] !== t27) {
      t20 = classNames(styles["review-container"], styles["review-container-with-radius"], t26, {
        [styles["review-container-mobile-carousel"]]: isInMobileCarousel,
        [styles["review-desktop-container"]]: isDesktopLayout,
        [styles["review-desktop-inline"]]: t27
      });
      $[64] = isDesktopLayout;
      $[65] = isInMobileCarousel;
      $[66] = t26;
      $[67] = t27;
      $[68] = t20;
    } else {
      t20 = $[68];
    }
    let t28;
    if ($[69] !== review.user.name) {
      t28 = <p className="ud-heading-md">{review.user.name}</p>;
      $[69] = review.user.name;
      $[70] = t28;
    } else {
      t28 = $[70];
    }
    const t29 = review.rating ?? 0;
    let t30;
    if ($[71] !== t29) {
      t30 = <StarRating rating={t29} size="medium" />;
      $[71] = t29;
      $[72] = t30;
    } else {
      t30 = $[72];
    }
    let t31;
    if ($[73] === Symbol.for("react.memo_cache_sentinel")) {
      t31 = classNames("ud-heading-xs", styles["time-since"]);
      $[73] = t31;
    } else {
      t31 = $[73];
    }
    let t32;
    if ($[74] !== review.createdAt) {
      t32 = <span className={t31}><RelativeDuration datetime={review.createdAt} /></span>;
      $[74] = review.createdAt;
      $[75] = t32;
    } else {
      t32 = $[75];
    }
    let t33;
    if ($[76] !== t30 || $[77] !== t32) {
      t33 = <div className={styles["rating-container"]}>{t30}{t32}</div>;
      $[76] = t30;
      $[77] = t32;
      $[78] = t33;
    } else {
      t33 = $[78];
    }
    let t34;
    if ($[79] !== t28 || $[80] !== t33) {
      t34 = <div className={styles["review-name-and-rating"]}>{t28}{t33}</div>;
      $[79] = t28;
      $[80] = t33;
      $[81] = t34;
    } else {
      t34 = $[81];
    }
    let t35;
    if ($[82] !== review.user) {
      t35 = <Avatar className={styles.avatar} data-purpose="avatar" user={review.user} srcKey="image_50x50" size="medium" alt="NONE" />;
      $[82] = review.user;
      $[83] = t35;
    } else {
      t35 = $[83];
    }
    let t36;
    if ($[84] !== t34 || $[85] !== t35) {
      t36 = <div className={styles["review-profile"]}>{t34}{t35}</div>;
      $[84] = t34;
      $[85] = t35;
      $[86] = t36;
    } else {
      t36 = $[86];
    }
    let t37;
    if ($[87] !== frontendTrackingId || $[88] !== isDesktopLayout || $[89] !== isOrgUser || $[90] !== review.id || $[91] !== review.user.name || $[92] !== reviewableObjectId || $[93] !== reviewableObjectType || $[94] !== shouldUseDeattachedDropdown) {
      t37 = isDesktopLayout && !isOrgUser && <ReviewReport reviewId={Number(review.id)} frontendTrackingId={frontendTrackingId} reviewableObjectType={reviewableObjectType} reviewableObjectId={reviewableObjectId} isFeaturedReview={false} shouldUseDeattachedDropdown={shouldUseDeattachedDropdown} reviewAuthorName={review.user.name} />;
      $[87] = frontendTrackingId;
      $[88] = isDesktopLayout;
      $[89] = isOrgUser;
      $[90] = review.id;
      $[91] = review.user.name;
      $[92] = reviewableObjectId;
      $[93] = reviewableObjectType;
      $[94] = shouldUseDeattachedDropdown;
      $[95] = t37;
    } else {
      t37 = $[95];
    }
    if ($[96] !== t36 || $[97] !== t37) {
      t21 = <div className={styles["review-heading"]}>{t36}{t37}</div>;
      $[96] = t36;
      $[97] = t37;
      $[98] = t21;
    } else {
      t21 = $[98];
    }
    t17 = styles["review-content-body"];
    t18 = renderReviewContent();
    $[26] = className;
    $[27] = frontendTrackingId;
    $[28] = gettext;
    $[29] = handleInlineShowMoreToggle;
    $[30] = handleShowMoreInModalClick;
    $[31] = instructorResponseVisibility;
    $[32] = isDesktopLayout;
    $[33] = isInMobileCarousel;
    $[34] = isModal;
    $[35] = isOrgUser;
    $[36] = review.content;
    $[37] = review.createdAt;
    $[38] = review.id;
    $[39] = review.rating;
    $[40] = review.response;
    $[41] = review.user;
    $[42] = reviewableObjectId;
    $[43] = reviewableObjectType;
    $[44] = searchedTerm;
    $[45] = setRefs;
    $[46] = shouldShowMoreButton;
    $[47] = shouldUseDeattachedDropdown;
    $[48] = shouldUseInlineShowMore;
    $[49] = trackReviewImpression;
    $[50] = T0;
    $[51] = renderInstructorResponse;
    $[52] = t17;
    $[53] = t18;
    $[54] = t19;
    $[55] = t20;
    $[56] = t21;
    $[57] = t22;
    $[58] = t23;
  } else {
    T0 = $[50];
    renderInstructorResponse = $[51];
    t17 = $[52];
    t18 = $[53];
    t19 = $[54];
    t20 = $[55];
    t21 = $[56];
    t22 = $[57];
    t23 = $[58];
  }
  let t24;
  if ($[99] !== t17 || $[100] !== t18) {
    t24 = <div className={t17}>{t18}</div>;
    $[99] = t17;
    $[100] = t18;
    $[101] = t24;
  } else {
    t24 = $[101];
  }
  const t25 = Number(review.id);
  let t26;
  if ($[102] !== frontendTrackingId || $[103] !== review.user.name || $[104] !== reviewableObjectId || $[105] !== reviewableObjectType || $[106] !== t25) {
    t26 = <ReviewFeedback reviewId={t25} frontendTrackingId={frontendTrackingId} reviewableObjectId={reviewableObjectId} reviewableObjectType={reviewableObjectType} isFeaturedReview={false} reviewAuthorName={review.user.name} />;
    $[102] = frontendTrackingId;
    $[103] = review.user.name;
    $[104] = reviewableObjectId;
    $[105] = reviewableObjectType;
    $[106] = t25;
    $[107] = t26;
  } else {
    t26 = $[107];
  }
  let t27;
  if ($[108] !== frontendTrackingId || $[109] !== isDesktopLayout || $[110] !== isOrgUser || $[111] !== review.id || $[112] !== review.user.name || $[113] !== reviewableObjectId || $[114] !== reviewableObjectType || $[115] !== shouldUseDeattachedDropdown) {
    t27 = !isDesktopLayout && !isOrgUser && <ReviewReport reviewId={Number(review.id)} frontendTrackingId={frontendTrackingId} reviewableObjectType={reviewableObjectType} reviewableObjectId={reviewableObjectId} isFeaturedReview={false} placement="top-end" shouldUseDeattachedDropdown={shouldUseDeattachedDropdown} reviewAuthorName={review.user.name} />;
    $[108] = frontendTrackingId;
    $[109] = isDesktopLayout;
    $[110] = isOrgUser;
    $[111] = review.id;
    $[112] = review.user.name;
    $[113] = reviewableObjectId;
    $[114] = reviewableObjectType;
    $[115] = shouldUseDeattachedDropdown;
    $[116] = t27;
  } else {
    t27 = $[116];
  }
  let t28;
  if ($[117] !== t26 || $[118] !== t27) {
    t28 = <div className={styles["review-feedback-and-report-container"]}>{t26}{t27}</div>;
    $[117] = t26;
    $[118] = t27;
    $[119] = t28;
  } else {
    t28 = $[119];
  }
  let t29;
  if ($[120] !== renderInstructorResponse) {
    t29 = renderInstructorResponse();
    $[120] = renderInstructorResponse;
    $[121] = t29;
  } else {
    t29 = $[121];
  }
  let t30;
  if ($[122] !== t19 || $[123] !== t20 || $[124] !== t21 || $[125] !== t24 || $[126] !== t28 || $[127] !== t29) {
    t30 = <div ref={t19} className={t20}>{t21}{t24}{t28}{t29}</div>;
    $[122] = t19;
    $[123] = t20;
    $[124] = t21;
    $[125] = t24;
    $[126] = t28;
    $[127] = t29;
    $[128] = t30;
  } else {
    t30 = $[128];
  }
  let t31;
  if ($[129] !== T0 || $[130] !== t22 || $[131] !== t30) {
    t31 = <T0 trackFunc={t22}>{t30}</T0>;
    $[129] = T0;
    $[130] = t22;
    $[131] = t30;
    $[132] = t31;
  } else {
    t31 = $[132];
  }
  let t32;
  if ($[133] !== t23 || $[134] !== t31) {
    t32 = <div className={t23}>{t31}</div>;
    $[133] = t23;
    $[134] = t31;
    $[135] = t32;
  } else {
    t32 = $[135];
  }
  return t32;
};
function _temp(n, t0) {
  const precision = t0 === undefined ? 5 : t0;
  return Math.round(n / precision) * precision;
}
function _temp2(line, index) {
  return <p key={index}>{line}</p>;
}